import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { useState } from "react";

interface ExportButtonProps {
  data: any[];
  filename: string;
  columns: { key: string; header: string; format?: (value: any) => string }[];
  disabled?: boolean;
}

export function ExportButton({ data, filename, columns, disabled }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  const formatValue = (value: any, format?: (v: any) => string): string => {
    if (value === null || value === undefined) return "";
    if (format) return format(value);
    if (typeof value === "number") return value.toString();
    if (value instanceof Date) return value.toISOString().split("T")[0];
    return String(value);
  };

  const exportToCSV = () => {
    setIsExporting(true);
    try {
      const headers = columns.map((col) => col.header);
      const rows = data.map((row) =>
        columns.map((col) => {
          const value = formatValue(row[col.key], col.format);
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (value.includes(",") || value.includes('"') || value.includes("\n")) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
      );

      const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } finally {
      setIsExporting(false);
    }
  };

  const exportToExcel = () => {
    setIsExporting(true);
    try {
      // Create a simple HTML table that Excel can open
      const headers = columns.map((col) => `<th>${col.header}</th>`).join("");
      const rows = data
        .map(
          (row) =>
            `<tr>${columns
              .map((col) => `<td>${formatValue(row[col.key], col.format)}</td>`)
              .join("")}</tr>`
        )
        .join("");

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="UTF-8"></head>
        <body>
          <table border="1">
            <thead><tr>${headers}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([html], { type: "application/vnd.ms-excel" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}_${new Date().toISOString().split("T")[0]}.xls`;
      link.click();
      URL.revokeObjectURL(link.href);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled || isExporting || !data?.length}>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportToCSV}>
          <FileText className="h-4 w-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportToExcel}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export as Excel
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
